import { useState, useEffect } from "react";
import { useBrands, useBrand, useUpdateBrand, useBrandVoice, useUpdateBrandVoice, useAddToCorpus } from "../hooks/useBrand";
import { BrandProfile } from "../components/brand/BrandProfile";
import { BrandVoice } from "../components/brand/BrandVoice";
import { BrandCorpus } from "../components/brand/BrandCorpus";
import { BrandExtraction } from "../components/brand/BrandExtraction";
import { SkillsList } from "../components/skills/SkillsList";
import { ConversationMonitor } from "../components/monitor/ConversationMonitor";
import { BrandConnections } from "../components/brand/BrandConnections";

type Tab = "profile" | "voice" | "corpus" | "skills" | "monitor" | "connections";

export function Brand() {
  // Read initial tab from URL query params (for OAuth return)
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && ["profile", "voice", "corpus", "skills", "monitor", "connections"].includes(t)) {
      return t as Tab;
    }
    return "profile" as Tab;
  })();

  const [tab, setTab] = useState<Tab>(initialTab);
  const { data: brandsData, isLoading } = useBrands();
  const brands = brandsData?.items?.map((item) => item.data) ?? [];
  const activeBrand = brands[0]; // For now, use first brand
  const brandId = activeBrand?.id;

  const { data: brandDetail } = useBrand(brandId);
  const brand = brandDetail?.data ?? activeBrand;

  const updateBrand = useUpdateBrand(brandId || "");
  const { data: voiceData } = useBrandVoice(brandId);
  const updateVoice = useUpdateBrandVoice(brandId || "");
  const addCorpus = useAddToCorpus(brandId || "");

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  // No brand yet — show extraction
  if (!brand) {
    return (
      <div className="mx-auto max-w-lg py-8">
        <h2 className="mb-6 text-xl font-bold text-slate-900">Set up your brand</h2>
        <BrandExtraction />
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "profile", label: "Profile" },
    { key: "voice", label: "Voice" },
    { key: "corpus", label: "Corpus" },
    { key: "skills", label: "Skills" },
    { key: "monitor", label: "Monitor" },
    { key: "connections", label: "Connect" },
  ];

  return (
    <div className="mx-auto max-w-lg py-4">
      <h2 className="mb-4 text-xl font-bold text-slate-900">{brand.name}</h2>

      <div className="mb-6 flex gap-1 rounded-lg bg-slate-100 p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <BrandProfile
          brand={brand}
          onSave={(data) => updateBrand.mutate(data)}
          saving={updateBrand.isPending}
        />
      )}
      {tab === "voice" && (
        <BrandVoice
          voice={voiceData?.data ?? null}
          onSave={(data) => updateVoice.mutate(data)}
          saving={updateVoice.isPending}
        />
      )}
      {tab === "corpus" && (
        <BrandCorpus
          onAdd={(content) => addCorpus.mutate({ content })}
          adding={addCorpus.isPending}
        />
      )}
      {tab === "skills" && brandId && (
        <SkillsList brandId={brandId} />
      )}
      {tab === "monitor" && brandId && (
        <ConversationMonitor brandId={brandId} />
      )}
      {tab === "connections" && brandId && (
        <BrandConnections brandId={brandId} />
      )}
    </div>
  );
}
